/**
 * Shared wishlist store — one code path across every product card,
 * collection page, and header badge.
 *
 * Any element with `data-wishlist-toggle` + `data-product-handle`
 * becomes a toggle button; click adds/removes the handle from the
 * `era-hugs:wishlist` localStorage set. All matching buttons on the
 * page sync their `aria-pressed` state, and a `wishlist:change` event
 * is dispatched on document so badges (header count, page banners,
 * etc.) can update live.
 *
 * Cross-tab: listens to `storage` events so a toggle in one tab
 * updates the count in others.
 *
 * Called from layout/theme.liquid via <script defer>.
 */
(() => {
  const KEY = 'era-hugs:wishlist';
  const EVENT = 'wishlist:change';

  const read = () => {
    try {
      return new Set(JSON.parse(localStorage.getItem(KEY) || '[]'));
    } catch (_) {
      return new Set();
    }
  };

  const write = (set) => {
    try {
      localStorage.setItem(KEY, JSON.stringify([...set]));
    } catch (_) { /* quota exceeded / private mode — swallow */ }
  };

  const dispatch = (set) => {
    document.dispatchEvent(new CustomEvent(EVENT, {
      detail: { handles: [...set], size: set.size },
    }));
  };

  const syncButtons = (set) => {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const handle = btn.dataset.productHandle;
      if (!handle) return;
      btn.setAttribute('aria-pressed', set.has(handle) ? 'true' : 'false');
    });
  };

  const toggle = (handle) => {
    const set = read();
    if (set.has(handle)) set.delete(handle); else set.add(handle);
    write(set);
    syncButtons(set);
    dispatch(set);
  };

  // Initial sync — button state reflects stored wishlist on page load.
  syncButtons(read());
  // Dispatch once so badges initialise from stored state without duplicating logic.
  dispatch(read());

  // Delegated click — works for any [data-wishlist-toggle], including
  // buttons added to the DOM after load (e.g. AJAX-loaded product grids).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    toggle(handle);
  });

  // Cross-tab sync — toggle in tab A updates tab B's badge and buttons.
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    const set = read();
    syncButtons(set);
    dispatch(set);
  });

  // Public API for scripts that need to read/toggle programmatically.
  window.EraHugsWishlist = { read, toggle, EVENT, KEY };
})();
